import React, { useLayoutEffect } from 'react';
import Ambassadors from './pages/Ambassadors';
import AmbassadorJoin from './pages/AmbassadorJoin';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { CartProvider } from './contexts/CartContext';
import { ErrorProvider } from './contexts/ErrorContext';
import Layout from './components/Layout';
import ErrorDisplay from './components/ErrorDisplay';
import APIErrorHandler from './components/APIErrorHandler';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Articles from './pages/Articles';
import Podcasts from './pages/Podcasts';
import PodcastDetail from './pages/PodcastDetail';
import VideoPodcasts from './pages/VideoPodcasts';
import CourseBrowse from './pages/CourseBrowse';
import Login from './pages/Login';
import Register from './pages/Register';
import UserDashboard from './pages/CustomerDashboard';
import Assessment from './pages/Assessment';
import VipSurvey from './pages/VipSurvey';
import BusinessAssessment from './pages/BusinessAssessment';
import RecruitmentApplication from './pages/RecruitmentApplication';
import PortalDashboardPage from './pages/portal/PortalDashboard';
import PortalTaskPage from './pages/portal/PortalTask';
import PortalSupportPage from './pages/portal/PortalSupport';
import PortalMessagesPage from './pages/portal/PortalMessages';
import ArticleDetail from './pages/ArticleDetail';
import CourseDetail from './pages/CourseDetail';
import CartPage from './pages/CartPage';
import VideoPlayer from './pages/VideoPlayer';
import AudioPlayer from './pages/AudioPlayer';
import VideoPodcastDetail from './pages/VideoPodcastDetail';
import WorkshopDetail from './pages/WorkshopDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentError from './pages/PaymentError';
import NotFound from './pages/NotFound';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import PurchaseGuide from './pages/PurchaseGuide';
import Partnership from './pages/Partnership';
import CareersPage from './pages/CareersPage';
import FAQPage from './pages/FAQPage';
import GlobalCompanions from './pages/GlobalCompanions';
import BookletLanding from './pages/BookletLanding';
import WorkshopShefayeZakhm from './pages/WorkshopShefayeZakhm';
import BlogLayout from './components/blog/BlogLayout';

/** با هر تغییر مسیر، اسکرول پنجره به بالای صفحه (رفتار معمول چندصفحه‌ای) */
function ScrollToTop() {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);
  return null;
}

/** آدرس قدیمی `/courses/browse` → `/courses` با حفظ query (مثل `?cat=`). */
function CoursesBrowseRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/courses', search }} replace />;
}

function App() {
  return (
    <ErrorProvider>
      <AuthProvider>
        <AudioPlayerProvider>
          <CartProvider>
            <APIErrorHandler />
            <ErrorDisplay />
            <Router>
              <ScrollToTop />
              <Routes>
                <Route path="/v/:code" element={<VipSurvey />} />
                <Route path="/free-book" element={<BookletLanding />} />
                {/* وبلاگ هدر و فوتر اختصاصی طرح خودش را دارد (بیرون از Layout سایت) */}
                <Route path="/articles" element={<BlogLayout variant="list"><Articles /></BlogLayout>} />
                <Route path="/articles/:slug" element={<BlogLayout variant="detail"><ArticleDetail /></BlogLayout>} />
                <Route path="*" element={
                  <Layout>
                    <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/podcasts" element={<Podcasts />} />
                  <Route path="/podcasts/:id" element={<PodcastDetail />} />
                  <Route path="/video-podcasts" element={<VideoPodcasts />} />
                  <Route path="/video-podcasts/:id" element={<VideoPodcastDetail />} />
                  <Route path="/courses/browse" element={<CoursesBrowseRedirect />} />
                  <Route path="/courses" element={<CourseBrowse />} />
                  <Route path="/courses/:courseId/videos/:videoId" element={<VideoPlayer />} />
                  <Route path="/courses/:courseId/audios/:audioId" element={<AudioPlayer />} />
                  <Route path="/courses/:id" element={<CourseDetail />} />
                <Route path="/cart" element={<CartPage />} />
                  <Route path="/workshops/:id" element={<WorkshopDetail />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/error" element={<PaymentError />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/guide" element={<PurchaseGuide />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/partnership" element={<Partnership />} />
                  <Route path="/companions-worldwide" element={<GlobalCompanions />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/kargah-shefaye-zakhm" element={<WorkshopShefayeZakhm />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/dashboard" element={<UserDashboard />} />
                  <Route path="/assessment" element={<Assessment />} />
                  <Route path="/a/:code" element={<Assessment />} />
                  {/* مسیر کوتاه پرسشنامهٔ مسیر رشد برای پیامک */}
                  <Route path="/roshd" element={<Assessment />} />
                  {/* مسیر کوتاه «تست سحر روانی» — قیف ورودی کارگاه شفای زخم */}
                  <Route path="/sehr" element={<Assessment defaultSlug="sehr" />} />
                  <Route path="/business-assessment" element={<BusinessAssessment />} />
                  <Route path="/careers/apply" element={<RecruitmentApplication />} />
                  <Route path="/portal" element={<PortalDashboardPage />} />
                  <Route path="/ambassadors" element={<Ambassadors />} />
                  <Route path="/ambassadors/join" element={<AmbassadorJoin />} />
                  <Route path="/portal/tasks/:assignmentId" element={<PortalTaskPage />} />
                  <Route path="/portal/messages" element={<PortalMessagesPage />} />
                  <Route path="/portal/questions" element={<PortalSupportPage />} />
                  <Route path="/portal/support" element={<PortalSupportPage />} />
                  {/* مسیر کوتاه برای پیامک — لینک‌های قدیمیِ بالا هم کار می‌کنند */}
                  <Route path="/6c" element={<BusinessAssessment />} />
                  <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                } />
              </Routes>
            </Router>
          </CartProvider>
        </AudioPlayerProvider>
      </AuthProvider>
    </ErrorProvider>
  );
}

export default App;
